import { db } from './db.js';
import { makeClient } from './clients.js';
import { subscriptionOf } from './subscription.js';
import { Emitter } from './utils.js';

/* Estado de cada proveedor guardado en el dispositivo (no solo del que está en uso): última
   respuesta de su panel, cuándo se comprobó y si la comprobación falló. Sirve a la lista de
   proveedores de Ajustes y a los avisos de caducidad. */

const KEY = 'provider-status';
const CHECK_TIMEOUT = 8000;

class Providers extends Emitter {
    constructor() {
        super();
        this.status = {};
        this.loaded = false;
        /** Para las pruebas: (profile) => Promise<{ user_info, server_info }>. */
        this.authFor = (profile) => makeClient(profile).auth();
    }

    async load() {
        if (this.loaded) return;
        this.status = (await db.get('meta', KEY).catch(() => null)) || {};
        this.loaded = true;
    }

    _save() {
        return db.put('meta', KEY, this.status).catch(() => {});
    }

    /** Guarda lo que acaba de decir el panel de un proveedor (p. ej. al cargar su catálogo). */
    record(profileId, account) {
        if (!profileId || !account || !account.user) return;
        this.status[profileId] = { time: Date.now(), account: { user: account.user, server: account.server || {} }, error: '' };
        this._save();
        this.emit('change', profileId);
    }

    /**
     * Pregunta al panel por el estado de la cuenta. Si no contesta, se conserva lo último que se
     * sabía y se anota el fallo: 'auth' (credenciales rechazadas) o 'network'.
     */
    async check(profile) {
        await this.load();
        if (!profile || profile.type === 'm3u') return this.get(profile);
        const prev = this.status[profile.id] || {};
        try {
            const data = await Promise.race([
                this.authFor(profile),
                new Promise((resolve, reject) => setTimeout(() => reject(new Error('timeout')), CHECK_TIMEOUT))
            ]);
            this.status[profile.id] = { time: Date.now(), account: { user: data.user_info || {}, server: data.server_info || {} }, error: '' };
        } catch (e) {
            this.status[profile.id] = { time: Date.now(), account: prev.account || null, error: e && e.code === 'auth' ? 'auth' : 'network' };
        }
        await this._save();
        this.emit('change', profile.id);
        return this.get(profile);
    }

    checkAll(list) {
        return Promise.all((list || []).map((p) => this.check(p).catch(() => null)));
    }

    /** { sub, error, checkedAt } de un proveedor con lo último que se sabe de él. */
    get(profile, { simulate = '' } = {}) {
        const s = (profile && this.status[profile.id]) || {};
        return { sub: subscriptionOf(s.account || null, { simulate }), error: s.error || '', checkedAt: s.time || 0 };
    }

    forget(profileId) {
        if (!this.status[profileId]) return;
        delete this.status[profileId];
        this._save();
    }
}

export const providers = new Providers();
